import { ScimAuthService } from './ScimAuthService';
import ImpersonationService from './ImpersonationService';

const CHATBOT_URL = process.env.REACT_APP_CHATBOT_URL || 'https://dcps-hades-ai-dev.azurewebsites.net';

export type ConnectionStatus = 'checking' | 'online' | 'offline' | 'auth-failed';

export interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: Date;
    isStreaming?: boolean;
    functionName?: string;
    profileCards?: Record<string, string>;
    htmlTables?: Record<string, string>;
}

export interface DonePayload {
    response: string;
    profileCards?: Record<string, string>;
    htmlTables?: Record<string, string>;
    debug?: Record<string, unknown>;
}

export interface StreamCallbacks {
    onContent: (text: string) => void;
    onFunctionCall: (name: string) => void;
    onDone: (payload: DonePayload) => void;
    onError: (error: string) => void;
}

let isAuthenticated = false;

export function getChatbotUrl(): string {
    return CHATBOT_URL;
}

export async function checkConnection(): Promise<ConnectionStatus> {
    try {
        const resp = await fetch(`${CHATBOT_URL}/api/health`, {
            method: 'GET',
            signal: AbortSignal.timeout(8000),
        });
        if (!resp.ok) return 'offline';
    } catch {
        return 'offline';
    }

    try {
        const token = await ScimAuthService.getAuthToken();
        if (!token) return 'auth-failed';

        const authResp = await fetch(`${CHATBOT_URL}/auth/shared`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ AccessToken: token }),
            signal: AbortSignal.timeout(10000),
        });
        if (!authResp.ok) return 'auth-failed';
        isAuthenticated = true;
        return 'online';
    } catch {
        return 'auth-failed';
    }
}

async function ensureAuth(): Promise<string> {
    const token = await ScimAuthService.getAuthToken();
    if (!token) throw new Error('No auth token available');

    if (!isAuthenticated) {
        const resp = await fetch(`${CHATBOT_URL}/auth/shared`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ AccessToken: token }),
        });
        if (!resp.ok) throw new Error(`Auth failed: ${resp.status}`);
        isAuthenticated = true;
    }
    return token;
}

export async function sendMessageStream(
    message: string,
    callbacks: StreamCallbacks,
    signal?: AbortSignal
): Promise<void> {
    const token = await ensureAuth();

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };

    const oboHeader = ImpersonationService.getOnBehalfOfHeader();
    Object.assign(headers, oboHeader);

    const response = await fetch(`${CHATBOT_URL}/api/chat/stream`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ message }),
        signal,
    });

    if (!response.ok) {
        if (response.status === 401) {
            isAuthenticated = false;
            callbacks.onError('Session expired. Please try again.');
            return;
        }
        let errorMsg = `Request failed (${response.status})`;
        try {
            const err = await response.json();
            errorMsg = err.error || errorMsg;
        } catch { /* ignore */ }
        callbacks.onError(errorMsg);
        return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
        callbacks.onError('No response stream');
        return;
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (!line.startsWith('data: ')) continue;
                let evt: Record<string, unknown>;
                try {
                    evt = JSON.parse(line.slice(6));
                } catch {
                    continue;
                }

                switch (evt.type) {
                    case 'content':
                        if (typeof evt.content === 'string') {
                            fullContent += evt.content;
                            callbacks.onContent(fullContent);
                        }
                        break;
                    case 'function_call':
                        if (typeof evt.functionName === 'string') {
                            callbacks.onFunctionCall(evt.functionName);
                        }
                        break;
                    case 'done':
                        callbacks.onDone({
                            response: (evt.response as string) || fullContent,
                            profileCards: evt.profileCards as Record<string, string> | undefined,
                            htmlTables: evt.htmlTables as Record<string, string> | undefined,
                            debug: evt.debug as Record<string, unknown> | undefined,
                        });
                        return;
                    case 'error':
                        callbacks.onError((evt.error as string) || 'Unknown error');
                        return;
                    case 'thinking':
                        break;
                }
            }
        }
        if (fullContent && !signal?.aborted) {
            callbacks.onDone({ response: fullContent });
        }
    } catch (err: unknown) {
        if (signal?.aborted) return;
        callbacks.onError(err instanceof Error ? err.message : 'Stream error');
    }
}

export async function clearChat(): Promise<void> {
    const token = await ensureAuth();
    await fetch(`${CHATBOT_URL}/api/chat/clear`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
    });
}

export function resetAuth(): void {
    isAuthenticated = false;
}
